import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Loaderdots from "../../components/LoaderDots/Loaderdots";
import "./Users.css";

const API_BASE = "https://admin.riymay.com/api/v1/admin";

const defaultFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  emailVerified: "no",
};

const Users = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isNextPage, setIsNextPage] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formState, setFormState] = useState(defaultFormState);
  const [savingProfile, setSavingProfile] = useState(false);
  const [togglingUser, setTogglingUser] = useState(false);
  const [removingAddressId, setRemovingAddressId] = useState(null);

  const token = localStorage.getItem("admintoken");

  const fetchUsers = async () => {
    try {
      setIsFetchingUsers(true);
      const response = await fetch(
        `${API_BASE}/user/search?query=${encodeURIComponent(searchQuery)}&page=${currentPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsersData(data.users || []);
      setIsNextPage(Boolean(data.nextPage));
      setTotalUsers(data.totalUsers || 0);
    } catch (error) {
      toast.error(error.message || "Failed to fetch users");
    } finally {
      setLoading(false);
      setIsFetchingUsers(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(`${API_BASE}/user/info?id=${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user details");
      }

      const data = await response.json();
      setSelectedUserId(userId);
      setSelectedUser(data);
      setFormState({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        email: data.email || "",
        phone: data.phone || "",
        emailVerified: data.emailVerified || "no",
      });
    } catch (error) {
      toast.error(error.message || "Failed to fetch user details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, currentPage]);

  const refreshSelectedUser = async () => {
    if (selectedUserId) {
      await fetchUserDetails(selectedUserId);
    }
    await fetchUsers();
  };

  const handleSearchChange = (event) => {
    setCurrentPage(1);
    setSearchQuery(event.target.value);
  };

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    if (!selectedUserId) return;

    try {
      setSavingProfile(true);
      const response = await fetch(`${API_BASE}/user/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: selectedUserId,
          ...formState,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to update user");
      }

      toast.success("User updated");
      setSelectedUser(data.user);
      await fetchUsers();
    } catch (error) {
      toast.error(error.message || "Failed to update user");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleBooleanAction = async (endpoint, payload, successMessage) => {
    try {
      setTogglingUser(true);
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || "Request failed");
      }

      toast.success(successMessage);
      setSelectedUser(data.user);
      await fetchUsers();
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setTogglingUser(false);
    }
  };

  const handleAddressRemove = async (addressId) => {
    if (!selectedUserId) return;

    try {
      setRemovingAddressId(addressId);
      const response = await fetch(`${API_BASE}/user/address`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: selectedUserId,
          addressId,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to remove address");
      }

      toast.success("Address removed");
      setSelectedUser(data.user);
      await fetchUsers();
    } catch (error) {
      toast.error(error.message || "Failed to remove address");
    } finally {
      setRemovingAddressId(null);
    }
  };

  if (loading) {
    return (
      <div>
        <Loaderdots />
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="users-header">
        <div>
          <h2>User Management</h2>
          <p>Search users, review account state, and apply safe admin actions.</p>
        </div>
        <button className="users-count-pill" type="button">
          Total users: {totalUsers}
        </button>
      </div>

      <div className="users-toolbar">
        <input
          type="text"
          placeholder="Search by name, email, or phone"
          value={searchQuery}
          onChange={handleSearchChange}
          className="users-search"
        />
        {isFetchingUsers && <span className="users-search-status">Updating results...</span>}
      </div>

      <div className="users-layout">
        <section className="users-list-panel">
          <div className="users-list-header users-grid-row">
            <span>Email</span>
            <span>Name</span>
            <span>Phone</span>
            <span>Status</span>
            <span>Actions</span>
          </div>

          {usersData.map((user) => (
            <div className="users-grid-row users-grid-item" key={user._id}>
              <button
                type="button"
                className="users-row-button"
                onClick={() => fetchUserDetails(user._id)}
              >
                {user.email}
              </button>
              <button
                type="button"
                className="users-row-button"
                onClick={() => fetchUserDetails(user._id)}
              >
                {user.firstName} {user.lastName}
              </button>
              <span>{user.phone || "Not set"}</span>
              <div className="users-status-group">
                <span className={`status-pill ${user.isBlocked ? "status-danger" : "status-ok"}`}>
                  {user.isBlocked ? "Blocked" : "Active"}
                </span>
                <span
                  className={`status-pill ${
                    user.passwordResetRequired ? "status-warning" : "status-neutral"
                  }`}
                >
                  {user.passwordResetRequired ? "Reset required" : "Reset clear"}
                </span>
              </div>
              <div className="users-inline-actions">
                <button type="button" onClick={() => fetchUserDetails(user._id)}>
                  Open
                </button>
                <Link to={`/user/${user._id}/orders`} className="users-orders-link">
                  Orders
                </Link>
              </div>
            </div>
          ))}

          {usersData.length === 0 && <div className="users-empty">No users found.</div>}

          <div className="users-pagination">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => page + 1)}
              disabled={!isNextPage}
            >
              Next
            </button>
          </div>
        </section>

        <aside className="users-detail-panel">
          {!selectedUser && !detailsLoading && (
            <div className="users-placeholder">
              Select a user to edit profile details, manage reset state, or remove an address.
            </div>
          )}

          {detailsLoading && <Loaderdots />}

          {selectedUser && !detailsLoading && (
            <>
              <div className="users-detail-header">
                <div>
                  <h3>{selectedUser.firstName} {selectedUser.lastName}</h3>
                  <p>{selectedUser.email}</p>
                </div>
                <div className="users-detail-actions">
                  <button
                    type="button"
                    disabled={togglingUser}
                    onClick={() =>
                      handleBooleanAction(
                        "/user/block",
                        { userId: selectedUser._id, isBlocked: !selectedUser.isBlocked },
                        selectedUser.isBlocked ? "User unblocked" : "User blocked"
                      )
                    }
                  >
                    {selectedUser.isBlocked ? "Unblock user" : "Block user"}
                  </button>
                  <button
                    type="button"
                    disabled={togglingUser}
                    onClick={() =>
                      handleBooleanAction(
                        "/user/password-reset-required",
                        {
                          userId: selectedUser._id,
                          passwordResetRequired: !selectedUser.passwordResetRequired,
                        },
                        selectedUser.passwordResetRequired
                          ? "Password reset cleared"
                          : "Password reset required"
                      )
                    }
                  >
                    {selectedUser.passwordResetRequired ? "Clear forced reset" : "Force password reset"}
                  </button>
                </div>
              </div>

              <form className="users-edit-form" onSubmit={handleProfileSave}>
                <label>
                  First name
                  <input
                    name="firstName"
                    value={formState.firstName}
                    onChange={handleProfileChange}
                    type="text"
                  />
                </label>
                <label>
                  Last name
                  <input
                    name="lastName"
                    value={formState.lastName}
                    onChange={handleProfileChange}
                    type="text"
                  />
                </label>
                <label>
                  Email
                  <input
                    name="email"
                    value={formState.email}
                    onChange={handleProfileChange}
                    type="email"
                  />
                </label>
                <label>
                  Phone
                  <input
                    name="phone"
                    value={formState.phone}
                    onChange={handleProfileChange}
                    type="text"
                  />
                </label>
                <label>
                  Email verified
                  <select
                    name="emailVerified"
                    value={formState.emailVerified}
                    onChange={handleProfileChange}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </label>

                <button type="submit" disabled={savingProfile}>
                  {savingProfile ? "Saving..." : "Save profile"}
                </button>
              </form>

              <div className="users-addresses">
                <div className="users-addresses-header">
                  <h4>Saved addresses</h4>
                  <button type="button" onClick={refreshSelectedUser}>
                    Refresh
                  </button>
                </div>

                {Array.isArray(selectedUser.shippingAddress) && selectedUser.shippingAddress.length > 0 ? (
                  selectedUser.shippingAddress.map((address) => (
                    <div key={address._id} className="users-address-card">
                      <div>
                        <strong>{address.houseNumber || "Address"}</strong>
                        <p>{address.street || "No street set"}</p>
                        <p>
                          {address.city || "City"}, {address.state || "State"} {address.pinCode || ""}
                        </p>
                        <p>{address.country || "Country"}</p>
                        {address.addressNarration && <p>{address.addressNarration}</p>}
                      </div>
                      <button
                        type="button"
                        disabled={removingAddressId === address._id}
                        onClick={() => handleAddressRemove(address._id)}
                      >
                        {removingAddressId === address._id ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="users-empty">No saved addresses.</div>
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Users;
